// Camera Bypass — shared constants & helpers (End User Web)
// Spec: Camera Bypass / Swinger Bypass / Temporary Disarm (Requirements Matrix v2.1)
// NOTE: No backend yet — the bypass store module holds mock data. This file only
// defines UI-facing constants and pure formatting helpers.

// Bypass brand color (defined by design). Mirrored in App.vue as --chekt-bypass.
export const BYPASS_COLOR = '#F5A623'
export const BYPASS_COLOR_RGB = '245,166,35'

// Below this remaining time we show a live running countdown (near);
// above it we show the absolute re-arm/end time (far). (PRD §7.5: "within about
// two hours" → running countdown.)
export const BYPASS_NEAR_THRESHOLD_MS = 2 * 60 * 60 * 1000 // 2 hours

// Editing/resetting a window is disallowed once ~1 minute or less remains: the
// window is about to expire/re-arm, so a duration change would race the ending.
// until-next-disarm windows have no expiry, so they stay editable.
export const EDIT_LOCK_MS = 60 * 1000 // 1 minute
export function isWindowEditable (record, now) {
  if (!record) return false
  if (record.mode === 'untilDisarm' || record.endTime == null) return true
  return (record.endTime - now) > EDIT_LOCK_MS
}

// Signal codes (reference — emitted by Bridge/FW, shown here for context only).
export const BYPASS_SIGNALS = {
  zone: { begin: 'E570', restore: 'R570' }, // Zone Bypass / Restoral
  zone24h: { begin: 'E572', restore: 'R572' }, // 24h Zone Bypass / Restoral (time required)
  swinger: { begin: 'E575', restore: 'R575' }, // Swinger Bypass start / clear (auto)
}

// Duration options for the picker. Which subset actually applies depends on the
// camera's zone type — see bypassDurationsForZone / bypassCustomMaxHours below:
//   • 24-hour zones (always armed): fixed durations up to 8h, no "Until Next
//     Disarm" (they never disarm). Custom capped at 8h.
//   • all other zones: up to 24h + Until Next Disarm. Custom capped at 24h.
//
// ⚠️ Backend dependency: the deployed site-api still validates duration as max
// 14400 (4h) (camera-bypass.validator.js). Durations above 4h will 400 until the
// drafted 86400 (24h) widening ships. The caps below follow the PRODUCT spec
// (CHEKT-1679) and need that backend deploy to work end-to-end.
export const BYPASS_DURATIONS = [
  { key: '1min', label: '1 min', ms: 1 * 60 * 1000 },
  { key: '30min', label: '30 min', ms: 30 * 60 * 1000 },
  { key: '1h', label: '1 hour', ms: 1 * 60 * 60 * 1000 },
  { key: '4h', label: '4 hours', ms: 4 * 60 * 60 * 1000 },
  { key: '8h', label: '8 hours', ms: 8 * 60 * 60 * 1000 },
  { key: '24h', label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { key: 'untilDisarm', label: 'Until Next Disarm', ms: null },
  { key: 'custom', label: 'Custom', ms: null },
]

// Zone-type duration ceilings (seconds). A 24-hour zone is always armed, so it
// gets a shorter cap and no "until next disarm".
export const BYPASS_MAX_FIXED_SEC_24H_ZONE = 8 * 60 * 60   // 28800 (8h)
export const BYPASS_MAX_FIXED_SEC_DEFAULT = 24 * 60 * 60    // 86400 (24h)

// siteDevice.js zone_type values that are always-armed 24-hour zones:
// 3 = 24hour, 22 = intercom 24h.
export function is24HourZone (zoneType) {
  return Number(zoneType) === 3 || Number(zoneType) === 22
}

// Max fixed bypass seconds allowed for a camera, by its zone type.
export function bypassMaxFixedSec (zoneType) {
  return is24HourZone(zoneType) ? BYPASS_MAX_FIXED_SEC_24H_ZONE : BYPASS_MAX_FIXED_SEC_DEFAULT
}

// Custom-duration hours cap for a camera, by its zone type (8h vs 24h).
export function bypassCustomMaxHours (zoneType) {
  return is24HourZone(zoneType) ? 8 : 24
}

// Picker options allowed for a camera, by its zone type: drop presets above the
// zone cap, and drop "Until Next Disarm" on 24-hour zones. "Custom" always stays.
export function bypassDurationsForZone (zoneType) {
  const maxSec = bypassMaxFixedSec(zoneType)
  const is24h = is24HourZone(zoneType)
  return BYPASS_DURATIONS.filter(function (d) {
    if (d.key === 'custom') return true
    if (d.key === 'untilDisarm') return !is24h
    return d.ms != null && Math.round(d.ms / 1000) <= maxSec
  })
}

export function getBypassDuration (key) {
  return BYPASS_DURATIONS.find(d => d.key === key) || null
}

// Resolve a selected duration into concrete { endTime, label, mode }.
// startTime & customMs are supplied by the caller (store) — this stays pure.
export function resolveBypassDuration (key, startTime, customMs) {
  if (key === 'untilDisarm') {
    return { endTime: null, mode: 'untilDisarm', label: 'Until Next Disarm' }
  }
  if (key === 'custom') {
    const ms = Math.max(0, customMs || 0)
    return {
      endTime: startTime + ms,
      mode: 'duration',
      label: formatDurationLabel(ms),
    }
  }
  const d = getBypassDuration(key)
  if (!d || d.ms == null) return { endTime: null, mode: 'untilDisarm', label: 'Until Next Disarm' }
  return { endTime: startTime + d.ms, mode: 'duration', label: d.label }
}

function pad (n) {
  return n < 10 ? '0' + n : '' + n
}

// Human label for an arbitrary duration in ms (used for custom durations).
export function formatDurationLabel (ms) {
  const totalMin = Math.round(ms / 60000)
  if (totalMin < 60) return `${totalMin} min`
  const hours = Math.floor(totalMin / 60)
  const mins = totalMin % 60
  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`
  return `${hours}h ${mins}m`
}

// Live countdown text for a remaining span (near state).
// Under a minute we show "a minute" instead of ticking seconds: the second-level
// value can drift from the real re-arm/end moment, so the soft phrase avoids a
// misleading 0:03 → 0:00 that doesn't line up with what actually happens. Reads as
// "Re-arms in a minute" / "a minute left" in the surrounding copy.
export function formatCountdown (remainingMs) {
  if (remainingMs <= 0) return '0:00'
  if (remainingMs < 60000) return 'a minute'
  const totalSec = Math.floor(remainingMs / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
  return `${m}:${pad(s)}`
}

// Countdown for a pending approval request's expiry (short timeout, ~2 min).
// Unlike the active-bypass countdown this keeps ticking real seconds — the
// operator/owner needs the urgency — but under 10s we soften to "a few seconds"
// (reads as "a few seconds left"), since exact seconds there are just noise.
export function formatApprovalTimeout (remainingMs) {
  if (remainingMs <= 0) return '0:00'
  if (remainingMs <= 10000) return 'a few seconds'
  const totalSec = Math.floor(remainingMs / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${pad(s)}`
}

// Remaining-time text — we deliberately never show an absolute end clock/date.
// An absolute time raises "which timezone?" questions and adds nothing the
// remaining time doesn't already say. Near the end we tick (m:ss / h:mm:ss);
// further out we show a coarse "4 hours" / "23h 45m" so a long bypass isn't a
// second-by-second distraction.
export function formatRemaining (remainingMs) {
  if (remainingMs < BYPASS_NEAR_THRESHOLD_MS) return formatCountdown(remainingMs)
  return formatDurationLabel(remainingMs)
}

// Short status text for a bypass badge/pill given the current time.
// Always relative: countdown when near, coarse remaining when far,
// untilDisarm => "Until Disarm".
export function bypassStatusText (record, now) {
  if (!record) return ''
  if (record.status === 'awaiting') return 'Awaiting'
  if (record.status === 'pre-arm') return 'Pre-Arm'
  if (record.mode === 'untilDisarm' || record.endTime == null) return 'Until Disarm'
  const remaining = record.endTime - now
  if (remaining <= 0) return 'Expiring'
  return formatRemaining(remaining)
}

// Longer, human descriptive text for list rows (Spec §4 status display).
export function bypassDetailText (record, now) {
  if (!record) return ''
  if (record.status === 'awaiting') return 'Awaiting approval'
  if (record.status === 'pre-arm') return 'Re-activates on next arm'
  if (record.mode === 'untilDisarm' || record.endTime == null) return 'Bypassed until next disarm'
  const remaining = record.endTime - now
  if (remaining <= 0) return 'Expiring…'
  return `Re-arms in ${formatRemaining(remaining)}`
}

// ── API ↔ store mapping (real backend, group ② site-api) ─────────────────────
// The store/UI record shape predates the API. These map the snake_case API
// entities (camera_bypass / approval_request) onto that record shape so the
// getters and components work unchanged.

// Server time → epoch ms. Returns null for null/invalid (the ticker treats null
// endTime as "until next disarm").
// Backend times are UTC, but site-api's arming-status sends the bare
// "YYYY-MM-DD HH:MM:SS" shape (no 'T', no 'Z') — Date.parse would read that as
// LOCAL time and drift the countdown by the client's timezone offset. Normalize
// any timezone-less value to explicit UTC before parsing.
export function parseServerTime (iso) {
  if (!iso) return null
  let s = String(iso)
  if (s.indexOf('T') === -1 && s.indexOf(' ') !== -1) s = s.replace(' ', 'T')
  if (s.indexOf('T') !== -1 && !/[Zz]$/.test(s) && !/[+-]\d\d:?\d\d$/.test(s)) s += 'Z'
  const t = Date.parse(s)
  return Number.isNaN(t) ? null : t
}

// API camera_bypass → record. Active bypass → status 'bypassed'; a bypass that
// carries ended_at → 'ended' (caller drops it).
// siteId: the authoritative site is known from the request URL — callers pass it
// so records are always scoped correctly. The list endpoint often omits site_id
// on items (or returns it as a number); site-scoped getters compare with a
// STRING selectedSiteId, so we coerce to String to keep === matching. (#1)
export function mapCameraBypass (cb, siteId) {
  if (!cb) return null
  const start = parseServerTime(cb.starts_at)
  const end = cb.until_next_disarm ? null : parseServerTime(cb.expires_at)
  const isApp = cb.created_by_type === 'app_user'
  const sid = siteId != null ? siteId : cb.site_id
  return {
    id: cb.id,
    deviceId: cb.device_id,
    siteId: sid != null ? String(sid) : null,
    status: cb.ended_at ? 'ended' : 'bypassed',
    mode: cb.until_next_disarm ? 'untilDisarm' : 'duration',
    startTime: start,
    endTime: end,
    durationKey: null,
    durationLabel: cb.until_next_disarm
      ? 'Until Next Disarm'
      : (start != null && end != null ? formatDurationLabel(end - start) : ''),
    initiator: isApp ? 'app user' : 'operator',
    initiatorName: cb.created_by_label || (isApp ? 'You' : 'Central Station'),
    reason: cb.reason_label || '',
    approvalRequestId: cb.approval_request_id || null,
    replacedBypassId: cb.replaced_bypass_id || null,
    endedAt: parseServerTime(cb.ended_at),
    endReason: cb.end_reason || null,
    requestExpiresAt: null,
    requestedCustomMs: 0,
    raw: cb,
  }
}

// API approval_request (action=bypass) → an 'awaiting' record.
// siteId is stamped from the known request site (see mapCameraBypass). (#1)
export function mapBypassApprovalRequest (ar, siteId) {
  if (!ar) return null
  const isApp = ar.requested_by_type === 'app_user'
  const durMs = (ar.requested_duration || 0) * 1000
  const sid = siteId != null ? siteId : ar.site_id
  return {
    id: ar.id,
    deviceId: ar.device_id,
    siteId: sid != null ? String(sid) : null,
    status: 'awaiting',
    // 'bypass' = MC asks to bypass; 'clear_bypass' = MC asks to clear an active
    // bypass. The two are routed to different store maps (a device can have both).
    action: ar.action || 'bypass',
    mode: ar.until_next_disarm ? 'untilDisarm' : 'duration',
    startTime: parseServerTime(ar.created_at),
    endTime: null,
    durationKey: null,
    durationLabel: ar.until_next_disarm ? 'Until Next Disarm' : (durMs ? formatDurationLabel(durMs) : ''),
    initiator: isApp ? 'app user' : 'operator',
    initiatorName: ar.requested_by_label || 'Central Station',
    reason: ar.reason_label || '',
    approvalRequestId: ar.id,
    requestExpiresAt: parseServerTime(ar.expires_at),
    requestedCustomMs: durMs,
    raw: ar,
  }
}

// Picker duration key (+customMs) → API create/edit payload
// ({ duration } in seconds, XOR { untilNextDisarm: true }).
// "Until Next Disarm" comes ONLY from the explicit untilDisarm option. A fixed
// duration is clamped to [60, maxFixedSec] — a fixed timer stays a fixed timer,
// never silently turned into an indefinite bypass. maxFixedSec is the caller's
// zone-aware ceiling (bypassMaxFixedSec); it defaults to the non-24h-zone 24h cap.
export function bypassDurationPayload (key, customMs, maxFixedSec) {
  if (key === 'untilDisarm') return { untilNextDisarm: true }
  const cap = maxFixedSec || BYPASS_MAX_FIXED_SEC_DEFAULT
  const sec = key === 'custom'
    ? Math.round(Math.max(0, customMs || 0) / 1000)
    : (() => { const d = getBypassDuration(key); return d && d.ms != null ? Math.round(d.ms / 1000) : null })()
  // Unresolvable (unknown key / non-positive custom) — upstream canConfirm blocks
  // this; send neither key so the server rejects it rather than guessing a value.
  if (sec == null || sec <= 0) return {}
  // Clamp to [60, cap]; presets/custom are multiples of 60, and 60 + the caps
  // (28800/86400) are multiples of 60, so the step stays valid.
  return { duration: Math.min(cap, Math.max(60, sec)) }
}

// Attribution label for who initiated the bypass (Spec §3 banner attribution).
export function bypassInitiatorLabel (record) {
  if (!record) return ''
  switch (record.initiator) {
    case 'operator': return record.initiatorName || 'Central Station'
    case 'swinger': return 'Swinger (auto)'
    case 'app user':
    default: return record.initiatorName || 'You'
  }
}
